export const getEventListPageStrapiURL = (
  locale?: string,
  slug?: string,
  citySlug?: string,
  categorySlug?: string
) => {
  const eventListPageURLQuery = new URLSearchParams()

  eventListPageURLQuery.set('populate[showcase][populate][0]', 'first.image')
  eventListPageURLQuery.set('populate[showcase][populate][1]', 'second.image')
  eventListPageURLQuery.set('populate[showcase][populate][2]', 'third.image')
  eventListPageURLQuery.set('populate[horizontalCardList][populate]', 'image')

  // GiftcardSection - only populate specific fields needed
  eventListPageURLQuery.set('populate[giftcardSection][fields][0]', 'title')
  eventListPageURLQuery.set(
    'populate[giftcardSection][fields][1]',
    'sectionTitle'
  )
  eventListPageURLQuery.set('populate[giftcardSection][fields][2]', 'subtitle')

  // Localizations - only populate minimal fields needed for hreflang links and merging
  // We fetch for the current locale only, but localizations field contains minimal data for hreflang links
  // Only need: id (for merging), locale, slug, citySlug, categorySlug, type (for getKonfettiPagePath)
  eventListPageURLQuery.set('populate[localizations][fields][0]', 'id')
  eventListPageURLQuery.set('populate[localizations][fields][1]', 'locale')
  eventListPageURLQuery.set('populate[localizations][fields][2]', 'slug')
  eventListPageURLQuery.set('populate[localizations][fields][3]', 'citySlug')
  eventListPageURLQuery.set(
    'populate[localizations][fields][4]',
    'categorySlug'
  )
  eventListPageURLQuery.set('populate[localizations][fields][5]', 'type')

  // SEO - populate specific fields and filter metaImage
  // Only include fields that exist in Strapi SEO component and are actually used
  eventListPageURLQuery.set('populate[seo][fields][0]', 'metaTitle')
  eventListPageURLQuery.set('populate[seo][fields][1]', 'metaDescription')
  eventListPageURLQuery.set('populate[seo][fields][2]', 'keywords')
  eventListPageURLQuery.set(
    'populate[seo][populate][metaImage][fields][0]',
    'formats'
  )
  eventListPageURLQuery.set(
    'populate[seo][populate][metaImage][fields][1]',
    'url'
  )
  eventListPageURLQuery.set(
    'populate[seo][populate][metaImage][fields][2]',
    'alternativeText'
  )

  // SearchFilters - only populate tags field
  eventListPageURLQuery.set('populate[searchFilters][fields][0]', 'tags')

  // add slug for tag and teamevent pages
  slug && eventListPageURLQuery.set('filters[slug][$eq]', slug)
  // set city slug for city and city category pages
  citySlug && eventListPageURLQuery.set('filters[citySlug][$eq]', citySlug)
  // set city slug for category and city category pages
  categorySlug &&
    eventListPageURLQuery.set('filters[categorySlug][$eq]', categorySlug)
  // set city slug as null to avoid city pages
  !citySlug && eventListPageURLQuery.set('filters[citySlug][$null]', 'true')
  // set category slug as null to avoid city pages
  !categorySlug &&
    eventListPageURLQuery.set('filters[categorySlug][$null]', 'true')

  if (locale) {
    eventListPageURLQuery.set('locale', locale)
  }

  return 'event-list-pages?' + eventListPageURLQuery.toString()
}

export const getStrapiArticlesCategoryUrl = (slug: string, locale: string) => {
  const articlesCategoryURL = new URLSearchParams()

  articlesCategoryURL.set('filters[slug][$eq]', slug)
  articlesCategoryURL.set('populate[summary_list][populate]', '*')

  // Articles in category - populate relations with field filtering
  // Filter article header_image fields - only formats and url needed
  articlesCategoryURL.set(
    'populate[articles][populate][header_image][fields][0]',
    'formats'
  )
  articlesCategoryURL.set(
    'populate[articles][populate][header_image][fields][1]',
    'url'
  )

  // Filter article admin_user fields - only name fields needed for author
  articlesCategoryURL.set(
    'populate[articles][populate][admin_user][fields][0]',
    'firstname'
  )
  articlesCategoryURL.set(
    'populate[articles][populate][admin_user][fields][1]',
    'lastname'
  )

  if (locale) {
    articlesCategoryURL.set('locale', locale)
  }

  return 'categories?' + articlesCategoryURL.toString()
}

export const getStrapiPartnerArticlesUrl = (
  supplierId: string,
  locale: string
) => {
  const partnerArticlesURL = new URLSearchParams()

  // Filter by supplier_id
  partnerArticlesURL.set('filters[supplier_id][$eq]', supplierId)

  // Filter article fields - only id, slug, and title needed for PartnerArticles component
  partnerArticlesURL.set('fields[0]', 'id')
  partnerArticlesURL.set('fields[1]', 'slug')
  partnerArticlesURL.set('fields[2]', 'title')

  // Set locale
  if (locale) {
    partnerArticlesURL.set('locale', locale)
  }

  // Sort by id descending
  partnerArticlesURL.set('sort[0]', 'id:desc')

  return 'articles?' + partnerArticlesURL.toString()
}

export const getStrapiArticlesUrl = (
  slug?: string,
  locale?: string,
  query?: Record<string, string>
) => {
  const articlesURL = new URLSearchParams()

  // Filter by slug if provided
  if (slug) {
    articlesURL.set('filters[slug][$eq]', slug)
  }

  // Set locale
  if (locale) {
    articlesURL.set('locale', locale)
  }

  // Metrics field - needed for placeholder replacement (entity + value for API call)
  articlesURL.set('populate[metrics]', '*')

  // Populate relations with field filtering to reduce payload
  // Header image - populate and filter fields
  articlesURL.set('populate[header_image][fields][0]', 'formats')
  articlesURL.set('populate[header_image][fields][1]', 'url')
  articlesURL.set('populate[header_image][fields][2]', 'alternativeText')

  // Admin user - only name fields needed for author
  articlesURL.set('populate[admin_user][fields][0]', 'firstname')
  articlesURL.set('populate[admin_user][fields][1]', 'lastname')

  // Categories - only slug and title needed
  articlesURL.set('populate[categories][fields][0]', 'slug')
  articlesURL.set('populate[categories][fields][1]', 'title')

  // SEO - use wildcard to populate all SEO fields and relations
  articlesURL.set('populate[seo][populate]', '*')

  // Author information - use wildcard (as suggested to avoid field name issues)
  articlesURL.set('populate[author_information][populate]', '*')

  // Article showcase - populate articles relation with header_image and admin_user
  // Filter article header_image fields - only formats and url needed
  articlesURL.set(
    'populate[article_showcase][populate][articles][populate][header_image][fields][0]',
    'formats'
  )
  articlesURL.set(
    'populate[article_showcase][populate][articles][populate][header_image][fields][1]',
    'url'
  )
  // Filter article admin_user fields - only name fields needed for author
  articlesURL.set(
    'populate[article_showcase][populate][articles][populate][admin_user][fields][0]',
    'firstname'
  )
  articlesURL.set(
    'populate[article_showcase][populate][articles][populate][admin_user][fields][1]',
    'lastname'
  )

  // FAQ - use wildcard (typically small)
  articlesURL.set('populate[faq][populate]', '*')

  // Summary list - use wildcard
  articlesURL.set('populate[summary_list][populate]', '*')

  // Add any additional query parameters (like pagination, sort, etc.)
  if (query) {
    Object.keys(query).forEach((key) => {
      // Skip pLevel as we're doing explicit population instead
      const value = query[key]
      if (key !== 'pLevel' && value) {
        articlesURL.set(key, value)
      }
    })
  }

  return 'articles?' + articlesURL.toString()
}

export const getStrapiPageURL = (
  baseURL: string,
  locale: string,
  slug: string
) => {
  const pageURL = new URL(`pages?populate[contents][populate]=*`, baseURL)
  slug && pageURL.searchParams.set('filters[slug][$eq]', slug)
  pageURL.searchParams?.set('locale', locale || 'all')

  // ?populate[contents][populate]=*
  // ?populate[shared.carousel][populate][items][populate]=*&populate[title]=*&populate[CTA]=*
  // GET /api/teamevents?populate[carousel][populate][items][populate]=*&populate[title]=*&populate[CTA]=*
  // &populate[contents][on][shared.carousel][populate]=image
  return pageURL
}

/**
 * Get optimized Strapi URL for newsletter single-type
 * Replaces pLevel=4 with explicit field selection for better performance
 */
export const getNewsletterStrapiURL = (locale?: string) => {
  const newsletterURLQuery = new URLSearchParams()

  // Only populate the fields that are actually used in the component
  // Fields: title, subtitle, placeholder, buttonLabel, media
  newsletterURLQuery.set('fields[0]', 'title')
  newsletterURLQuery.set('fields[1]', 'subtitle')
  newsletterURLQuery.set('fields[2]', 'placeholder')
  newsletterURLQuery.set('fields[3]', 'buttonLabel')

  // Media - populate only link and minimal image fields
  // Each media item needs: link, and image (only url and alternativeText)
  // Note: getImageUrl() is called without format parameter, so it only uses url
  // formats is not needed, saving significant payload size
  newsletterURLQuery.set('populate[media][fields][0]', 'link')
  newsletterURLQuery.set('populate[media][populate][image][fields][0]', 'url')
  newsletterURLQuery.set(
    'populate[media][populate][image][fields][1]',
    'alternativeText'
  )

  if (locale) {
    newsletterURLQuery.set('locale', locale)
  }

  return 'newsletter?' + newsletterURLQuery.toString()
}

/**
 * Get optimized Strapi URL for homepage single-type
 * Replaces pLevel=5 with explicit field selection for better performance
 */
export const getHomePageStrapiURL = (locale?: string) => {
  const homepageParams = new URLSearchParams()

  if (locale) {
    homepageParams.set('locale', locale)
  }

  // Banner - populate mainMediaBlock and secondaryMediaBlocks with image filtering
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][populate][media][fields][0]',
    'formats'
  )
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][populate][media][fields][1]',
    'url'
  )
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][populate][media][fields][2]',
    'alternativeText'
  )
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][populate][mediaMobile][fields][0]',
    'formats'
  )
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][populate][mediaMobile][fields][1]',
    'url'
  )
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][fields][0]',
    'title'
  )
  homepageParams.set(
    'populate[banner][populate][mainMediaBlock][fields][1]',
    'subtitle'
  )
  homepageParams.set(
    'populate[banner][populate][secondaryMediaBlocks][populate][media][fields][0]',
    'formats'
  )
  homepageParams.set(
    'populate[banner][populate][secondaryMediaBlocks][populate][media][fields][1]',
    'url'
  )
  homepageParams.set(
    'populate[banner][populate][secondaryMediaBlocks][populate][media][fields][2]',
    'mime'
  )
  homepageParams.set(
    'populate[banner][populate][secondaryMediaBlocks][populate][media][fields][3]',
    'alternativeText'
  )
  homepageParams.set(
    'populate[banner][populate][secondaryMediaBlocks][populate][mediaMobile][fields][0]',
    'formats'
  )
  homepageParams.set(
    'populate[banner][populate][secondaryMediaBlocks][populate][mediaMobile][fields][1]',
    'url'
  )

  // CardSection - populate cards with image filtering
  homepageParams.set('populate[cardSection][fields][0]', 'title')
  homepageParams.set('populate[cardSection][fields][1]', 'subtitle')
  homepageParams.set(
    'populate[cardSection][populate][cards][fields][0]',
    'title'
  )
  homepageParams.set(
    'populate[cardSection][populate][cards][fields][1]',
    'description'
  )
  homepageParams.set(
    'populate[cardSection][populate][cards][fields][2]',
    'link'
  )
  homepageParams.set(
    'populate[cardSection][populate][cards][populate][image][fields][0]',
    'url'
  )
  homepageParams.set(
    'populate[cardSection][populate][cards][populate][image][fields][1]',
    'alternativeText'
  )

  // GridSection - populate items with image and CTA filtering
  homepageParams.set('populate[gridSection][fields][0]', 'title')
  homepageParams.set('populate[gridSection][fields][1]', 'subtitle')
  homepageParams.set('populate[gridSection][fields][2]', 'background')
  homepageParams.set('populate[gridSection][populate][items][fields][0]', 'id')
  homepageParams.set(
    'populate[gridSection][populate][items][fields][1]',
    'caption'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][2]',
    'title'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][3]',
    'content'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][4]',
    'background'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][5]',
    'imagePosition'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][6]',
    'rows'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][7]',
    'columns'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][fields][8]',
    'alignment'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][populate][image][fields][0]',
    'url'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][populate][CTA][fields][0]',
    'label'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][populate][CTA][fields][1]',
    'color'
  )
  homepageParams.set(
    'populate[gridSection][populate][items][populate][CTA][fields][2]',
    'url'
  )

  // Carousel - populate items with image filtering
  homepageParams.set('populate[carousel][populate][items][fields][0]', 'link')
  homepageParams.set('populate[carousel][populate][items][fields][1]', 'title')
  homepageParams.set(
    'populate[carousel][populate][items][fields][2]',
    'description'
  )
  homepageParams.set(
    'populate[carousel][populate][items][populate][image][fields][0]',
    'url'
  )
  homepageParams.set(
    'populate[carousel][populate][items][populate][image][fields][1]',
    'alternativeText'
  )

  return 'home-page?' + homepageParams.toString()
}
